import MediaPreview from 'components/MediaPreview/MediaPreview';
import { StyledTooltip } from 'components/InfoTooltip/InfoTooltip';
import PreviewViewSwitcher from 'components/PreviewViewSwitcher/PreviewViewSwitcher';
import { useField } from 'formik';
import { heicTo, isHeic } from 'heic-to';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDropZone from 'react-dropzone';
import { FiCheck, FiX } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import ReactCrop, { centerCrop, convertToPixelCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { toastr } from 'utils';
import { getCroppedImg } from 'utils/crop';
import Placeholder from '../../../../assets/img/covers/placeholder.jpg';
import MediaCropPreview from './MediaCropPreview';
import * as Ui from './MediaUpload.styles';

const CROP_MEDIA = {
  banner: { aspect: 16 / 6, ratioLabel: '16:6', recommendedKey: 'mediaCrop.recommendedBanner' },
  look: { aspect: 4 / 5, ratioLabel: '4:5', recommendedKey: 'mediaCrop.recommendedLook' },
  product: { aspect: 1, ratioLabel: '1:1', recommendedKey: 'mediaCrop.recommendedProduct' },
};

const getCropAspect = (mode, cropMediaType) => {
  if (mode === 'square') return 1;
  if (mode === 'recommended') return CROP_MEDIA[cropMediaType]?.aspect ?? 1;
  return undefined;
};

const getCropForMode = (mode, cropMediaType, width, height) => {
  const aspect = getCropAspect(mode, cropMediaType);
  if (aspect) {
    return centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 100,
        },
        aspect,
        width,
        height,
      ),
      width,
      height,
    );
  }

  if (mode === 'rectangle') {
    return {
      unit: '%',
      x: 0,
      y: 25,
      width: 100,
      height: 50,
    };
  }

  return undefined;
};

const appendMediaFiles = (currentFiles, incoming, maxFiles) => {
  const files = Array.isArray(incoming) ? incoming : [incoming];
  return currentFiles.length === maxFiles
    ? [...currentFiles.slice(files.length), ...files]
    : [...currentFiles, ...files];
};

const cropTooltipProps = {
  placement: 'top',
  PopperProps: { style: { zIndex: 10001 } },
};

export const MediaUpload = ({
  maxFiles = 6,
  accept = { 'image/*': [] },
  withCrop = false,
  withThumbnails = true,
  onUpload,
  hintMessage = '',
  setFile,
  defaultCropMode = 'square',
  cropMediaType,
  previewData,
  ...props
}) => {
  const { t } = useTranslation('app');
  const initialCropMode = cropMediaType ? 'recommended' : defaultCropMode;
  const [uploadingFile, setUploadingFile] = useState(null);
  const [crop, setCrop] = useState();
  const imgRef = useRef(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [selectedCropMode, setSelectedCropMode] = useState(initialCropMode);
  const [cropFitMode, setCropFitMode] = useState('fill');
  const fillCropStateRef = useRef({
    mode: initialCropMode,
    crop: undefined,
  });
  const [workspaceView, setWorkspaceView] = useState('crop');
  const [generatedPreview, setGeneratedPreview] = useState(null);
  const generatedPreviewRef = useRef(null);
  const previewRequestRef = useRef(0);
  const [field, meta, { setValue }] = useField(props);
  const [{ value: cloudinaryImages }] = useField({ name: props.imageField || 'images' });
  const isInvalid = meta.touched && meta.error;
  const fieldFiles = useMemo(() => {
    if (Array.isArray(field.value)) return field.value;
    return field.value ? [field.value] : [];
  }, [field.value]);
  const hasPreview = Boolean(cropMediaType);
  const isStrictFit = cropFitMode === 'fit';

  const clearGeneratedPreview = useCallback(() => {
    previewRequestRef.current += 1;
    if (generatedPreviewRef.current) {
      URL.revokeObjectURL(generatedPreviewRef.current);
      generatedPreviewRef.current = null;
    }
    setGeneratedPreview(null);
  }, []);

  const applyCrop = (modeOrCrop, image = imgRef.current) => {
    if (!image) return;
    const nextCrop =
      typeof modeOrCrop === 'string'
        ? getCropForMode(modeOrCrop, cropMediaType, image.width, image.height)
        : modeOrCrop;
    if (!nextCrop) return;
    setCrop(nextCrop);
    setCompletedCrop(convertToPixelCrop(nextCrop, image.width, image.height));
  };

  const onDropAccepted = useCallback(
    async (droppedFiles) => {
      const isVideo = droppedFiles[0]?.type?.startsWith('video');
      const filesToProcess = withCrop && !isVideo ? droppedFiles.slice(0, 1) : droppedFiles;
      const acceptedFiles = (
        await Promise.all(
          filesToProcess.map(async (file) => {
            const fileIsHeic = await isHeic(file).catch(() => false);
            if (!fileIsHeic) return file;

            try {
              const convertedBlob = await heicTo({ blob: file, type: 'image/jpeg', quality: 1 });
              return new File([convertedBlob], file.name, { type: 'image/jpeg' });
            } catch {
              toastr.error(t('toastr:error'), t('heicConversionError'));
              return null;
            }
          }),
        )
      ).filter(Boolean);

      if (!acceptedFiles.length) return;

      if (!isVideo && withCrop) {
        clearGeneratedPreview();
        fillCropStateRef.current = { mode: initialCropMode, crop: undefined };
        setSelectedCropMode(initialCropMode);
        setCropFitMode('fill');
        setWorkspaceView('crop');
        setCrop(undefined);
        setCompletedCrop(null);
        setUploadingFile(acceptedFiles[0]);
        return;
      }

      onUpload && onUpload(acceptedFiles[0]);
      setValue(appendMediaFiles(fieldFiles, acceptedFiles, maxFiles));
    },
    [
      fieldFiles,
      setValue,
      onUpload,
      maxFiles,
      withCrop,
      cropMediaType,
      initialCropMode,
      t,
      clearGeneratedPreview,
    ],
  );

  const onDropRejected = useCallback(
    (rejectedFiles) =>
      rejectedFiles[0]?.errors.forEach(({ message }) => toastr.error(t('toastr:error'), message)),
    [t],
  );

  const handleCropModeChange = (mode) => {
    if (cropFitMode === 'fit') return;
    setSelectedCropMode(mode);
    applyCrop(mode);
  };

  const handleCropFitModeChange = (mode) => {
    if (mode === cropFitMode) return;

    const image = imgRef.current;

    if (mode === 'fit') {
      fillCropStateRef.current = { mode: selectedCropMode, crop };
      setCropFitMode('fit');
      if (cropMediaType) {
        setSelectedCropMode('recommended');
        applyCrop('recommended');
      }
      return;
    }

    const restoredMode = fillCropStateRef.current.mode;
    setCropFitMode('fill');
    setSelectedCropMode(restoredMode);

    if (!image) return;
    applyCrop(
      fillCropStateRef.current.crop ||
        getCropForMode(restoredMode, cropMediaType, image.width, image.height) ||
        getCropForMode('recommended', cropMediaType, image.width, image.height),
    );
  };

  const handleFileDelete = (file) => {
    const isCloudinary = !file.path && !file.size;
    setValue(fieldFiles.filter((item) => (isCloudinary ? item !== file : item.name !== file.name)));
  };

  const cropSubmit = async () => {
    const img = imgRef.current;
    if (!uploadingFile || (!isStrictFit && (!img || !completedCrop))) {
      toastr.error('Error', 'Could not crop the image.');
      return;
    }

    let file = uploadingFile;
    if (!isStrictFit) {
      const image = await getCroppedImg(img, completedCrop, uploadingFile.name, uploadingFile.type);
      file = new File([image], image.name, { type: uploadingFile.type });
    }

    setValue(appendMediaFiles(fieldFiles, file, maxFiles));
    onUpload && onUpload(file);
    setUploadingFile(null);
  };

  const uploadingFileUrl = useMemo(
    () => (uploadingFile ? URL.createObjectURL(uploadingFile) : undefined),
    [uploadingFile],
  );

  useEffect(
    () => () => uploadingFileUrl && URL.revokeObjectURL(uploadingFileUrl),
    [uploadingFileUrl],
  );

  useEffect(() => {
    if (!uploadingFile) {
      clearGeneratedPreview();
      return undefined;
    }

    const image = imgRef.current;
    if (isStrictFit || !hasPreview || !image || !completedCrop) {
      return undefined;
    }

    const requestId = previewRequestRef.current + 1;
    previewRequestRef.current = requestId;

    getCroppedImg(image, completedCrop, uploadingFile.name, uploadingFile.type)
      .then((previewBlob) => {
        if (!previewBlob || previewRequestRef.current !== requestId) return;

        const previewUrl = URL.createObjectURL(previewBlob);
        if (generatedPreviewRef.current) {
          URL.revokeObjectURL(generatedPreviewRef.current);
        }
        generatedPreviewRef.current = previewUrl;
        setGeneratedPreview(previewUrl);
      })
      .catch(() => null);

    return () => {
      if (previewRequestRef.current === requestId) {
        previewRequestRef.current += 1;
      }
    };
  }, [clearGeneratedPreview, completedCrop, hasPreview, isStrictFit, uploadingFile]);

  useEffect(() => () => clearGeneratedPreview(), [clearGeneratedPreview]);

  const getCloudinaryPreview = useCallback(
    (file) =>
      cloudinaryImages?.find((imageUrl) => imageUrl.indexOf(file) > -1)?.replace('pdf', 'png') ||
      (cloudinaryImages?.length && cloudinaryImages[0]?.replace('pdf', 'png')) ||
      Placeholder,
    [cloudinaryImages],
  );

  const previewItems = useMemo(
    () =>
      fieldFiles.filter(Boolean).map((file) => {
        const isCloudinary = !file.path && !file.size;
        return {
          file,
          src: isCloudinary ? getCloudinaryPreview(file) : URL.createObjectURL(file),
          type: file?.type?.startsWith('video') ? 'video' : 'image',
          isObjectUrl: !isCloudinary,
        };
      }),
    [fieldFiles, getCloudinaryPreview],
  );

  useEffect(
    () => () => {
      previewItems.forEach(({ isObjectUrl, src }) => {
        if (isObjectUrl) {
          URL.revokeObjectURL(src);
        }
      });
    },
    [previewItems],
  );

  useEffect(() => {
    if (!setFile) return;
    setFile(previewItems[0]?.src ?? null);
  }, [previewItems, setFile]);

  const hasImages = previewItems.length > 0;
  const cropMedia = CROP_MEDIA[cropMediaType];
  const cropModes = [
    cropMedia && {
      id: 'recommended',
      title: t(cropMedia.recommendedKey),
      label: t('mediaCrop.recommendedLabel'),
      icon: <Ui.RatioLabel aria-hidden="true">{cropMedia.ratioLabel}</Ui.RatioLabel>,
    },
    {
      id: 'manual',
      title: t('mediaCrop.manual'),
      label: t('mediaCrop.manualLabel'),
      icon: <Ui.RatioShape $manual $width={22} $height={17} aria-hidden="true" />,
    },
    {
      id: 'square',
      title: t('mediaCrop.square'),
      label: t('mediaCrop.squareLabel'),
      icon: <Ui.RatioShape $width={20} $height={20} aria-hidden="true" />,
    },
    {
      id: 'rectangle',
      title: t('mediaCrop.rectangle'),
      label: t('mediaCrop.rectangleLabel'),
      icon: <Ui.RatioShape $width={25} $height={16} aria-hidden="true" />,
    },
  ].filter(Boolean);
  const cropFitModes = [
    { id: 'fit', title: t('mediaCrop.fit'), label: t('mediaCrop.fitLabel') },
    { id: 'fill', title: t('mediaCrop.fill'), label: t('mediaCrop.fillLabel') },
  ];

  const cropEditor = (
    <Ui.CropEditor>
      <ReactCrop
        crop={isStrictFit ? undefined : crop}
        disabled={isStrictFit}
        onChange={(pixelCrop, percentCrop) => {
          if (selectedCropMode === 'rectangle') {
            setCrop({ ...percentCrop, width: 100, x: 0 });
          } else {
            setCrop(percentCrop);
          }
        }}
        onComplete={(currentCrop) => setCompletedCrop(currentCrop)}
        aspect={getCropAspect(selectedCropMode, cropMediaType)}
        minHeight={100}
        minWidth={100}
      >
        <img
          ref={imgRef}
          alt={t('mediaCrop.imageAlt')}
          src={uploadingFileUrl}
          onLoad={(e) => applyCrop(selectedCropMode, e.currentTarget)}
        />
      </ReactCrop>
    </Ui.CropEditor>
  );

  const cropToolbar = (
    <Ui.CropToolbar role="toolbar" aria-label={t('mediaCrop.toolbarLabel')}>
      <StyledTooltip title={t('mediaCrop.cancel')} {...cropTooltipProps}>
        <Ui.CropAction
          type="button"
          aria-label={t('mediaCrop.cancel')}
          onClick={() => setUploadingFile(null)}
        >
          <FiX size={22} />
        </Ui.CropAction>
      </StyledTooltip>
      {cropModes.map((mode) => (
        <StyledTooltip key={mode.id} title={mode.title} {...cropTooltipProps}>
          <Ui.CropMode
            type="button"
            disabled={isStrictFit}
            $selected={selectedCropMode === mode.id}
            aria-label={mode.title}
            aria-pressed={selectedCropMode === mode.id}
            onClick={() => handleCropModeChange(mode.id)}
          >
            {mode.icon}
            <Ui.CropModeLabel aria-hidden="true">{mode.label}</Ui.CropModeLabel>
          </Ui.CropMode>
        </StyledTooltip>
      ))}
      <Ui.CropToolbarDivider aria-hidden="true" />
      {cropFitModes.map((mode) => (
        <StyledTooltip key={mode.id} title={mode.title} {...cropTooltipProps}>
          <Ui.CropFitMode
            type="button"
            $selected={cropFitMode === mode.id}
            aria-label={mode.title}
            aria-pressed={cropFitMode === mode.id}
            onClick={() => handleCropFitModeChange(mode.id)}
          >
            {mode.label}
          </Ui.CropFitMode>
        </StyledTooltip>
      ))}
      <Ui.CropToolbarDivider aria-hidden="true" />
      <StyledTooltip title={t('mediaCrop.apply')} {...cropTooltipProps}>
        <Ui.CropAction type="button" aria-label={t('mediaCrop.apply')} onClick={cropSubmit}>
          <FiCheck size={22} strokeWidth={3} />
        </Ui.CropAction>
      </StyledTooltip>
    </Ui.CropToolbar>
  );

  return (
    <>
      {!props.disabled && (
        <>
          {uploadingFile && withCrop ? (
            <Ui.CropOverlay $hasPreview={hasPreview}>
              {hasPreview && (
                <PreviewViewSwitcher
                  activeView={workspaceView}
                  ariaLabel={t('mediaCrop.workspaceToolbarLabel')}
                  floating
                  onChange={setWorkspaceView}
                  primaryLabel={t('crop')}
                  primaryView="crop"
                  previewLabel={t('preview')}
                />
              )}
              <Ui.CropWorkspace $hasPreview={hasPreview}>
                <Ui.CropPanel $active={!hasPreview || workspaceView === 'crop'}>
                  {cropEditor}
                </Ui.CropPanel>
                {hasPreview && (
                  <Ui.PreviewPanel $active={workspaceView === 'preview'}>
                    <Ui.PreviewSurface>
                      <MediaCropPreview
                        cropMediaType={cropMediaType}
                        previewData={previewData}
                        previewImage={
                          isStrictFit ? uploadingFileUrl : generatedPreview || uploadingFileUrl
                        }
                      />
                    </Ui.PreviewSurface>
                  </Ui.PreviewPanel>
                )}
              </Ui.CropWorkspace>
              {cropToolbar}
            </Ui.CropOverlay>
          ) : (
            <ReactDropZone
              accept={accept}
              maxFiles={maxFiles}
              multiple={maxFiles > 1}
              onDropAccepted={onDropAccepted}
              onDropRejected={onDropRejected}
            >
              {({ getRootProps, getInputProps }) => (
                <Ui.Dropzone id="dropzone-wrapper" {...getRootProps()}>
                  <input id="dropzone" {...getInputProps()} disabled={props.disabled} />
                  <p className="text-muted text-center">
                    {maxFiles === 1 ? t('dropzonePlaceholderSingle') : t('dropzonePlaceholder')}
                    {!!hintMessage && (
                      <>
                        <br /> {hintMessage}
                      </>
                    )}
                  </p>
                </Ui.Dropzone>
              )}
            </ReactDropZone>
          )}
          {isInvalid ? <Ui.ErrorFeedback>{meta.error}</Ui.ErrorFeedback> : null}
        </>
      )}
      {withThumbnails && hasImages && (
        <Ui.PreviewThumbnailWrapper>
          {previewItems.map(({ file, src, type }) => (
            <Ui.PreviewThumbnail key={file?.name || file}>
              {!props.disabled && (
                <button
                  type="button"
                  className="btn-white rounded-circle"
                  disabled={props.disabled}
                  onClick={() => handleFileDelete(file)}
                  style={{ backgroundColor: '#FFFFFF' }}
                >
                  <i className="fe fe-x" />
                </button>
              )}
              <MediaPreview
                src={src}
                alt={t('uploadedMediaPreview')}
                type={type}
                dark={props.dark}
                autoplay
              />
            </Ui.PreviewThumbnail>
          ))}
        </Ui.PreviewThumbnailWrapper>
      )}
    </>
  );
};

MediaUpload.propTypes = {
  maxFiles: PropTypes.number,
  disabled: PropTypes.bool,
  accept: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.array]),
  dark: PropTypes.bool,
  withCrop: PropTypes.bool,
  withThumbnails: PropTypes.bool,
  onUpload: PropTypes.func,
  hintMessage: PropTypes.string,
  setFile: PropTypes.func,
  imageField: PropTypes.string,
  defaultCropMode: PropTypes.oneOf(['square', 'rectangle']),
  cropMediaType: PropTypes.oneOf(['banner', 'look', 'product']),
  previewData: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    currency: PropTypes.string,
    size: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    color: PropTypes.object,
    products: PropTypes.arrayOf(PropTypes.object),
    originImage: PropTypes.string,
  }),
};
